import {
  alpha,
  Box,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AxiosError, AxiosResponse } from "axios";
import {
  TransactionDetailsData,
  TransactionDetailsResponse,
} from "reduxStore/dataTypes";
import { fetchTransactionDetails } from "services/apiService";
import { formateTransactionDate } from "utils/dateFormat";
import { getStatusBgColor } from "utils/common";
import { formatNumber } from "utils/formatter";
import theme, { responsiveFontSize } from "themes/Theme";
import { commonStyle } from "../common.styles";

const TransactionDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [txnData, setTxnData] = useState<TransactionDetailsData | null>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getTransactionDetails = async () => {
    try {
      if (!id) return;
      const response: AxiosResponse<TransactionDetailsResponse> =
        await fetchTransactionDetails(id);
      if (response?.status === 200) {
        setTxnData(response?.data?.data);
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error?.response?.data?.message);
      }
    }
  };

  useEffect(() => {
    if (id) getTransactionDetails();
  }, [id]);

  return (
    <Box>
      <Box sx={commonStyle.cardStyle}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          mb={2}
          gap={1}
          flexWrap="wrap"
        >
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ color: theme.palette.text.primary }} />
          </IconButton>
          <Typography sx={styles.headingTitle}>Transaction Details</Typography>
        </Stack>

        {/* Hash + Date */}
        <Stack
          direction={isMobile ? "column" : "row"}
          justifyContent="space-between"
          gap={isMobile ? 2 : 0}
        >
          <Box sx={{ wordBreak: "break-all" }}>
            <Typography sx={styles.titleText}>Transaction Hash</Typography>
            <Typography sx={styles.valueText}>{txnData?.txHash}</Typography>
          </Box>
          <Box sx={{ textAlign: isMobile ? "left" : "right" }}>
            <Typography sx={styles.titleText}>Transaction Date</Typography>
            <Typography sx={styles.valueText}>
              {txnData?.createdAt
                ? formateTransactionDate(txnData.createdAt)
                : "-"}
            </Typography>
          </Box>
        </Stack>

        {/* From */}
        <Typography sx={styles.titleText} mt={3}>
          Transaction From
        </Typography>
        <Typography sx={{ ...styles.valueText, wordBreak: "break-all" }}>
          {txnData?.fromAddress}
        </Typography>

        {/* To */}
        <Typography sx={styles.titleText} mt={3}>
          Transaction To
        </Typography>
        <Typography sx={{ ...styles.valueText, wordBreak: "break-all" }}>
          {txnData?.toAddress}
        </Typography>

        {/* Status */}
        <Typography sx={styles.titleText} mt={3}>
          Status
        </Typography>
        <Box
          sx={{
            border: `1px solid ${getStatusBgColor(
              txnData?.status?.toLowerCase()
            )}`,
            backgroundColor: alpha(
              getStatusBgColor(txnData?.status?.toLowerCase()),
              0.2
            ),
            px: 1,
            py: 0.5,
            borderRadius: "6px",
            width: "fit-content",
          }}
        >
          <Typography
            sx={{
              ...styles.valueText,
              textTransform: "capitalize",
              color: getStatusBgColor(txnData?.status?.toLowerCase()),
            }}
          >
            {txnData?.status}
          </Typography>
        </Box>

        {/* Amount */}
        <Typography sx={styles.titleText} mt={3}>
          Transaction Amount
        </Typography>
        <Stack direction="row" alignItems="baseline" gap={0.4}>
          <Typography sx={styles.amountValueText}>
            {txnData?.amount ? formatNumber(Number(txnData.amount)) : "-"}
          </Typography>
          <Typography sx={styles.amountUnitText}>
            {txnData?.cryptoTicker || "-"}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

export default TransactionDetails;

const styles = {
  headingTitle: {
    color: theme.palette.text.primary,
    fontSize: responsiveFontSize(16),
    fontWeight: 400,
  },
  titleText: {
    color: theme.palette.primary.main,
    fontSize: responsiveFontSize(16),
    fontWeight: 400,
  },
  valueText: {
    color: theme.palette.text.primary,
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
  },
  amountValueText: {
    color: theme.palette.text.primary,
    fontSize: responsiveFontSize(20),
    fontWeight: 400,
  },
  amountUnitText: {
    color: theme.palette.primary.main,
    fontSize: responsiveFontSize(14),
    fontWeight: 400,
  },
};
